import React, { useState } from 'react';
import Papa from 'papaparse';

const BatchCSVUploadComponent = ({ firebase }) => {
    const [file, setFile] = useState(null);
    const [processing, setProcessing] = useState(false);
    const [results, setResults] = useState([]);
    const [progress, setProgress] = useState(0);
    const [statusMessage, setStatusMessage] = useState('');
    const [summary, setSummary] = useState({ total: 0, successful: 0, failed: 0 });
    const [uidOrEmailList, setUidOrEmailList] = useState('');
    const [filteredEmails, setFilteredEmails] = useState([]);
    const [fetchingEmails, setFetchingEmails] = useState(false);
    const [inputType, setInputType] = useState(''); // 'emails' or 'uids'

    const API_ENDPOINT = 'https://europe-west3-digitalna-kartica.cloudfunctions.net/addNewScanWithEmailAsKey2nd';
    const COMPANY_UID = 'Io9YuAnCoSO4DmM4zXrfQcSocnq2';

    // Batch size for API calls
    const BATCH_SIZE = 100; // Process 100 records per API call

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            setFile(e.target.files[0]);
            setResults([]);
            setProgress(0);
            setSummary({ total: 0, successful: 0, failed: 0 });
        }
    };

    const handleUidOrEmailListChange = (e) => {
        const value = e.target.value;
        setUidOrEmailList(value);
        setFilteredEmails([]); // Clear previous emails when list changes
        setInputType(''); // Reset input type detection
    };

    const detectInputType = (inputList) => {
        if (!inputList.trim()) return null;

        const items = inputList.split(',').map(item => item.trim()).filter(item => item.length > 0);
        if (items.length === 0) return null;

        // Check if any item contains @ symbol (likely email)
        const hasEmails = items.some(item => item.includes('@'));
        return hasEmails ? 'emails' : 'uids';
    };

    const processUidOrEmailList = async () => {
        if (!uidOrEmailList.trim()) {
            alert('Please enter UIDs or emails');
            return;
        }

        const detectedType = detectInputType(uidOrEmailList);
        if (!detectedType) {
            alert('No valid UIDs or emails found');
            return;
        }

        setInputType(detectedType);

        if (detectedType === 'emails') {
            // Use emails directly
            const emails = uidOrEmailList.split(',').map(email => email.trim()).filter(email => email.length > 0);
            console.log(`Using ${emails.length} emails directly`);
            setFilteredEmails(emails);
            return;
        }

        // Process as UIDs - fetch emails from Firestore
        if (!firebase || !firebase.db) {
            alert('Firebase Firestore is not available');
            return;
        }

        setFetchingEmails(true);
        setFilteredEmails([]);

        try {
            // Parse UIDs from comma-separated string
            const uids = uidOrEmailList.split(',').map(uid => uid.trim()).filter(uid => uid.length > 0);

            console.log(`Fetching emails for ${uids.length} UIDs...`);

            // Firestore 'in' query supports up to 10 values, so we need to batch
            const batchSize = 10;
            const allEmails = [];

            for (let i = 0; i < uids.length; i += batchSize) {
                const uidBatch = uids.slice(i, i + batchSize);

                const query = firebase.db
                    .collection('users')
                    .where('uid', 'in', uidBatch);

                const snapshot = await query.get();

                snapshot.forEach(doc => {
                    const userData = doc.data();
                    if (userData.email) {
                        allEmails.push(userData.email);
                    }
                });
            }

            console.log(`Found ${allEmails.length} emails from ${uids.length} UIDs`);
            setFilteredEmails(allEmails);

            if (allEmails.length === 0) {
                alert('No emails found for the provided UIDs');
            }

        } catch (error) {
            console.error('Error fetching emails from Firestore:', error);
            alert('Failed to fetch emails: ' + error.message);
        } finally {
            setFetchingEmails(false);
        }
    };

    const parseOrderDate = (dateString) => {
        const date = new Date(dateString);
        return date.getTime();
    };

    const createItemsArray = (row) => {
        const items = [];
        for (let i = 1; i <= 5; i++) {
            const itemName = row[`Product Item ${i} Name`];
            const itemQuantity = row[`Product Item ${i} Quantity`];
            const itemTotal = row[`Product Item ${i} Total`];

            if (itemName && itemName.trim() !== '') {
                items.push({
                    name: itemName.trim(),
                    quantity: itemQuantity ? parseInt(itemQuantity) : 1,
                    total: itemTotal ? parseFloat(itemTotal) : 0
                });
            }
        }
        return items;
    };

    const convertRowToScanObject = (row) => {
        return {
            companyUid: COMPANY_UID,
            email: row.billing_email || '',
            vl: parseFloat(row.order_total) || 0,
            bn: row.order_id ? row.order_id.toString() : '',
            cc: 'STING',
            si: 'online',
            co: row.billing_country || '',
            ct: row.billing_city || '',
            ost: row.status || '',
            ut: row.order_date ? parseOrderDate(row.order_date) : Date.now(),
            items: createItemsArray(row),
            voucher: row.coupon_items || '',
        };
    };

    const processBatch = async (batch, idToken) => {
        try {
            const response = await fetch(API_ENDPOINT, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${idToken}`
                },
                body: JSON.stringify({
                    scans: batch
                })
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || `HTTP ${response.status}`);
            }

            return result;
        } catch (error) {
            console.error('Batch processing error:', error);
            // Return error result for all items in batch
            return {
                summary: { total: batch.length, successful: 0, failed: batch.length },
                results: batch.map((_, index) => ({
                    index,
                    success: false,
                    error: error.message
                }))
            };
        }
    };

    const processCSV = async () => {
        if (!file) {
            alert('Please select a CSV file');
            return;
        }

        if (!firebase || !firebase.currentUserAuth) {
            alert('Firebase is not available');
            return;
        }

        setProcessing(true);
        setResults([]);
        setProgress(0);
        setSummary({ total: 0, successful: 0, failed: 0 });
        setStatusMessage('Fetching authentication token...');

        try {
            console.log('Fetching authentication token...');
            const idToken = await firebase.currentUserAuth().getIdToken(false);
            console.log('Token fetched successfully');
            setStatusMessage('Authentication successful. Parsing CSV file...');

            Papa.parse(file, {
                header: true,
                dynamicTyping: true,
                skipEmptyLines: true,
                complete: async (result) => {
                    let rows = result.data;

                    // Filter rows by emails if UID filtering is enabled
                    if (filteredEmails.length > 0) {
                        const originalCount = rows.length;
                        rows = rows.filter(row => {
                            const email = row.billing_email;
                            return email && filteredEmails.includes(email);
                        });
                        console.log(`Filtered ${originalCount} rows to ${rows.length} based on UID email list`);
                        setStatusMessage(`Filtered to ${rows.length} rows from ${originalCount} total based on UID list`);
                    }

                    const totalRows = rows.length;

                    if (totalRows === 0) {
                        alert('No rows to process after filtering');
                        setProcessing(false);
                        setStatusMessage('');
                        return;
                    }

                    console.log(`Processing ${totalRows} rows in batches of ${BATCH_SIZE}...`);
                    setStatusMessage(`Processing ${totalRows} orders in batches...`);

                    // Convert all rows to scan objects
                    const scanObjects = rows.map(convertRowToScanObject);

                    let allResults = [];
                    let totalSuccessful = 0;
                    let totalFailed = 0;

                    // Process in batches
                    for (let i = 0; i < scanObjects.length; i += BATCH_SIZE) {
                        const batch = scanObjects.slice(i, i + BATCH_SIZE);
                        const batchNumber = Math.floor(i / BATCH_SIZE) + 1;
                        const totalBatches = Math.ceil(scanObjects.length / BATCH_SIZE);

                        setStatusMessage(`Processing batch ${batchNumber}/${totalBatches} (${batch.length} items)...`);

                        try {
                            // Refresh token every few batches to prevent expiration
                            let currentToken = idToken;
                            if (i > 0 && i % (BATCH_SIZE * 5) === 0) {
                                currentToken = await firebase.currentUserAuth().getIdToken(true);
                                console.log('Token refreshed');
                            }

                            const batchResult = await processBatch(batch, currentToken);

                            // Process batch results
                            if (batchResult.results) {
                                // Adjust indices to be global rather than batch-local
                                const adjustedResults = batchResult.results.map(result => ({
                                    ...result,
                                    index: i + result.index,
                                    batchNumber
                                }));

                                allResults.push(...adjustedResults);
                                totalSuccessful += batchResult.summary.successful;
                                totalFailed += batchResult.summary.failed;
                            }

                            // Update progress
                            const currentProgress = Math.round(((i + batch.length) / totalRows) * 100);
                            setProgress(currentProgress);

                            // Update summary
                            setSummary({
                                total: totalRows,
                                successful: totalSuccessful,
                                failed: totalFailed
                            });

                            // Update results display (show last 50)
                            setResults(allResults.slice(-50));

                            console.log(`Batch ${batchNumber}/${totalBatches} complete: ${batchResult.summary.successful}/${batch.length} successful`);

                            // Small delay between batches
                            if (i + batch.length < totalRows) {
                                await new Promise(resolve => setTimeout(resolve, 500));
                            }

                        } catch (error) {
                            console.error(`Error processing batch ${batchNumber}:`, error);

                            // Add failed results for this batch
                            const failedResults = batch.map((_, batchIndex) => ({
                                index: i + batchIndex,
                                success: false,
                                error: `Batch processing failed: ${error.message}`,
                                batchNumber
                            }));

                            allResults.push(...failedResults);
                            totalFailed += batch.length;

                            setSummary({
                                total: totalRows,
                                successful: totalSuccessful,
                                failed: totalFailed
                            });
                        }
                    }

                    // Store all results for download
                    setResults(allResults);
                    setProcessing(false);
                    setStatusMessage('');
                    console.log(`Processing complete! ${totalSuccessful}/${totalRows} successful`);
                },
                error: (error) => {
                    console.error('CSV parsing error:', error);
                    alert('Error parsing CSV file: ' + error.message);
                    setProcessing(false);
                    setStatusMessage('');
                }
            });
        } catch (error) {
            console.error('Error fetching token:', error);
            alert('Failed to authenticate: ' + error.message);
            setProcessing(false);
            setStatusMessage('');
        }
    };

    const downloadResults = () => {
        // Prepare results for CSV export
        const exportData = results.map(result => ({
            index: result.index,
            billNumber: result.billNumber || 'N/A',
            email: result.email || 'N/A',
            status: result.success ? 'Success' : 'Failed',
            error: result.error || '',
            batchNumber: result.batchNumber || 'N/A'
        }));

        const csvContent = Papa.unparse(exportData);
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `batch_processing_results_${new Date().toISOString()}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    };

    const clearEmailFilter = () => {
        setUidOrEmailList('');
        setFilteredEmails([]);
        setInputType('');
    };

    return (
        <div className="max-w-6xl mx-auto p-6">
            <h1 className="text-3xl font-bold mb-6">Batch CSV Order Upload</h1>

            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                <h2 className="text-xl font-semibold mb-4">Upload Configuration</h2>

                <div className="space-y-6">
                    {/* UID/Email Filter Section */}
                    <div className="border-b pb-4">
                        <h3 className="text-lg font-medium mb-3">Email Filter by UIDs or Emails (Optional)</h3>
                        <div className="space-y-3">
                            <div>
                                <label className="block text-sm font-medium mb-2">
                                    UIDs or Emails (comma-separated)
                                </label>
                                <textarea
                                    value={uidOrEmailList}
                                    onChange={handleUidOrEmailListChange}
                                    placeholder="uid1, uid2, uid3... OR email1@example.com, email2@example.com..."
                                    rows="3"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                <p className="mt-1 text-sm text-gray-500">
                                    Enter comma-separated UIDs (to fetch emails from Firestore) or emails directly to filter CSV rows
                                </p>
                            </div>

                            <div className="flex gap-3">
                                <button
                                    onClick={processUidOrEmailList}
                                    disabled={fetchingEmails || !uidOrEmailList.trim()}
                                    className={`px-4 py-2 rounded-md text-white font-medium ${
                                        fetchingEmails || !uidOrEmailList.trim()
                                            ? 'bg-gray-400 cursor-not-allowed'
                                            : 'bg-green-600 hover:bg-green-700'
                                    }`}
                                >
                                    {fetchingEmails ? 'Fetching Emails...' : 'Process UIDs/Emails'}
                                </button>

                                {filteredEmails.length > 0 && (
                                    <button
                                        onClick={clearEmailFilter}
                                        className="px-4 py-2 rounded-md text-gray-600 bg-gray-200 hover:bg-gray-300 font-medium"
                                    >
                                        Clear Filter
                                    </button>
                                )}
                            </div>

                            {filteredEmails.length > 0 && (
                                <div className="bg-green-50 p-3 rounded-md">
                                    <p className="text-sm font-medium text-green-800">
                                        ✓ {inputType === 'emails' ? `Using ${filteredEmails.length} emails directly` : `Found ${filteredEmails.length} emails from UIDs`}
                                    </p>
                                    <p className="text-xs text-green-600 mt-1">
                                        CSV will be filtered to only include rows with these emails
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* File Upload Section */}
                    <div>
                        <label className="block text-sm font-medium mb-2">CSV File</label>
                        <input
                            type="file"
                            accept=".csv"
                            onChange={handleFileChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        {file && (
                            <p className="mt-2 text-sm text-gray-600">
                                Selected: {file.name} ({(file.size / 1024).toFixed(2)} KB)
                            </p>
                        )}
                    </div>
                </div>

                <div className="mt-6">
                    <button
                        onClick={processCSV}
                        disabled={processing || !file}
                        className={`px-6 py-3 rounded-md text-white font-medium ${
                            processing || !file
                                ? 'bg-gray-400 cursor-not-allowed'
                                : 'bg-blue-600 hover:bg-blue-700'
                        }`}
                    >
                        {processing ? 'Processing Batches...' : 'Process CSV (Batch Mode)'}
                    </button>

                    {filteredEmails.length > 0 && (
                        <p className="mt-2 text-sm text-blue-600">
                            Will filter CSV to {filteredEmails.length} email addresses
                        </p>
                    )}
                </div>
            </div>

            {(processing || results.length > 0) && (
                <div className="bg-white rounded-lg shadow-md p-6">
                    <h2 className="text-xl font-semibold mb-4">Processing Progress</h2>

                    {statusMessage && (
                        <div className="mb-4 text-sm text-gray-600 bg-gray-50 p-3 rounded">
                            {statusMessage}
                        </div>
                    )}

                    <div className="mb-4">
                        <div className="flex justify-between mb-2">
                            <span className="text-sm font-medium">Progress</span>
                            <span className="text-sm font-medium">{progress}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                            <div
                                className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                                style={{ width: `${progress}%` }}
                            />
                        </div>
                    </div>

                    <div className="mb-4">
                        <h3 className="text-lg font-medium mb-2">Summary</h3><br/>
                        <div className="grid grid-cols-4 gap-4">
                            <div className="bg-blue-50 p-3 rounded">
                                <p className="text-sm text-gray-600">Total</p>
                                <p className="text-xl font-semibold text-blue-600">
                                    {summary.total}
                                </p>
                            </div>
                            <div className="bg-green-50 p-3 rounded">
                                <p className="text-sm text-gray-600">Successful</p>
                                <p className="text-xl font-semibold text-green-600">
                                    {summary.successful}
                                </p>
                            </div>
                            <div className="bg-red-50 p-3 rounded">
                                <p className="text-sm text-gray-600">Failed</p>
                                <p className="text-xl font-semibold text-red-600">
                                    {summary.failed}
                                </p>
                            </div>
                            <div className="bg-purple-50 p-3 rounded">
                                <p className="text-sm text-gray-600">Success Rate</p>
                                <p className="text-xl font-semibold text-purple-600">
                                    {summary.total > 0 ? Math.round((summary.successful / summary.total) * 100) : 0}%
                                </p>
                            </div>
                        </div>
                    </div>

                    {results.length > 0 && (
                        <>
                            <div className="mb-4">
                                <button
                                    onClick={downloadResults}
                                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                                >
                                    Download All Results CSV ({results.length} rows)
                                </button>
                            </div>

                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Index</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bill Number</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Batch</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Message</th>
                                    </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                    {results.slice(-20).map((result, index) => (
                                        <tr key={index}>
                                            <td className="px-4 py-3 text-sm">{result.index}</td>
                                            <td className="px-4 py-3 text-sm">{result.billNumber || 'N/A'}</td>
                                            <td className="px-4 py-3 text-sm">{result.email || 'N/A'}</td>
                                            <td className="px-4 py-3 text-sm">
                                                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                                                        result.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                                    }`}>
                                                        {result.success ? 'Success' : 'Failed'}
                                                    </span>
                                            </td>
                                            <td className="px-4 py-3 text-sm">{result.batchNumber || 'N/A'}</td>
                                            <td className="px-4 py-3 text-sm text-gray-600">{result.error || 'OK'}</td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                                <p className="text-sm text-gray-500 mt-2">
                                    Showing last 20 results of {results.length} total
                                </p>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default BatchCSVUploadComponent;